import { useTranslation } from "react-i18next";
import { z } from "zod";

export const MAX_BUDGET = 9999999;

export const useBudgetSchema = () => {
  const { t } = useTranslation();

  const budgetSchema = z.object({
    budget: z
      .number({
        error: (issue) =>
          issue.input === undefined
            ? t("errors.required")
            : t("errors.number", { field: t("common.budget") }),
      })
      .int({ error: t("errors.integer", { field: t("common.budget") }) })
      .min(0, { error: t("errors.budgetMin", { min: 0 }) })
      .max(MAX_BUDGET, { error: t("errors.budgetMax", { max: MAX_BUDGET }) }),
  });

  return { budgetSchema };
};

export type BudgetFormData = z.infer<
  ReturnType<typeof useBudgetSchema>["budgetSchema"]
>;
export type DebitFormData = z.infer<
  ReturnType<typeof useDebitSchema>["debitSchema"]
>;
export type CreditFormData = z.infer<
  ReturnType<typeof useCreditSchema>["creditSchema"]
>;

export const useClientMonthlyBudgetSchema = () => {
  const { t } = useTranslation();

  const clientMonthlyBudgetSchema = z.object({
    budget: z
      .number({
        error: (issue) =>
          issue.input === undefined
            ? t("errors.required")
            : t("errors.number", { field: t("common.budget") }),
      })
      .int({ error: t("errors.integer", { field: t("common.budget") }) })
      .min(0, { error: t("errors.budgetMin", { min: 0 }) })
      .max(MAX_BUDGET, { error: t("errors.budgetMax", { max: MAX_BUDGET }) }),
  });

  return { clientMonthlyBudgetSchema };
};

export const useClientCurrentMonthBudgetCapSchema = () => {
  const { t } = useTranslation();

  const clientCurrentMonthBudgetCapSchema = z.object({
    budget: z
      .number({
        error: (issue) =>
          issue.input === undefined
            ? t("errors.required")
            : t("errors.number", { field: t("common.budget") }),
      })
      .int({ error: t("errors.integer", { field: t("common.budget") }) })
      .min(1, { error: t("errors.budgetGreaterThanZero") })
      .max(MAX_BUDGET, { error: t("errors.budgetMax", { max: MAX_BUDGET }) }),
  });

  return { clientCurrentMonthBudgetCapSchema };
};

export const useDebitSchema = () => {
  const { t } = useTranslation();

  const debitSchema = z.object({
    budget: z
      .number({
        error: (issue) =>
          issue.input === undefined
            ? t("errors.required")
            : t("errors.debitAmountRequired"),
      })
      .int({ error: t("errors.integer", { field: t("common.debit") }) })
      .min(1, { error: t("errors.debitAmountMin", { min: 1 }) })
      .max(MAX_BUDGET, {
        error: t("errors.debitAmountMax", { max: MAX_BUDGET }),
      }),
    comments: z
      .string({ error: t("errors.commentsRequired") })
      .trim()
      .min(1, { error: t("errors.commentsRequired") })
      .max(200, { error: t("errors.commentsMax", { max: 200 }) }),
  });

  return { debitSchema };
};

export const useCreditSchema = () => {
  const { t } = useTranslation();

  const creditSchema = z.object({
    budget: z
      .number({
        error: (issue) =>
          issue.input === undefined
            ? t("errors.required")
            : t("errors.number", { field: t("common.credit") }),
      })
      .int({ error: t("errors.integer", { field: t("common.credit") }) })
      .min(1, { error: t("errors.creditAmountGreaterThanZero") })
      .max(MAX_BUDGET, {
        error: t("errors.creditAmountMax", { max: MAX_BUDGET }),
      }),
    comments: z
      .string({ error: t("errors.commentsRequired") })
      .trim()
      .min(1, { error: t("errors.commentsRequired") })
      .max(200, { error: t("errors.commentsMax", { max: 200 }) }),
  });

  return { creditSchema };
};
